import { Box, Grid, InputLabel } from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { ReactElement } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";

import { palette } from "~frontend/theme";

export function RhfDateTimePicker<T extends FieldValues>({
  name,
  dateLabel,
  timeLabel,
  optional = false,
  control,
}: {
  name: Path<T>;
  dateLabel: string;
  timeLabel: string;
  optional?: boolean;
  control: Control<T>;
}): ReactElement {
  const { field, fieldState } = useController({
    control,
    name,
  });

  // Parse the string value to DateTime, handling null/undefined
  const value =
    field.value && typeof field.value === "string"
      ? DateTime.fromISO(field.value)
      : null;

  function handleDateChange(newDate: DateTime | null) {
    if (!newDate) {
      field.onChange(null);
      return;
    }

    // Don't update if the date is invalid
    if (!newDate.isValid) {
      return;
    }

    if (value && value.isValid) {
      // Preserve time from existing value
      const combined = newDate.set({
        hour: value.hour,
        minute: value.minute,
        second: 0,
        millisecond: 0,
      });
      field.onChange(combined.toUTC().toISO());
    } else {
      // No existing value, set to start of day
      field.onChange(newDate.startOf("day").toUTC().toISO());
    }
  }

  function handleTimeChange(newTime: DateTime | null) {
    if (!newTime) {
      if (optional) {
        field.onChange(null);
      }
      return;
    }

    // Don't update if the time is invalid
    if (!newTime.isValid) {
      return;
    }

    if (value && value.isValid) {
      // Preserve date from existing value, update time
      const combined = value.set({
        hour: newTime.hour,
        minute: newTime.minute,
        second: 0,
        millisecond: 0,
      });
      field.onChange(combined.toUTC().toISO());
    } else {
      // No existing value, use today's date with selected time
      const combined = DateTime.now().set({
        hour: newTime.hour,
        minute: newTime.minute,
        second: 0,
        millisecond: 0,
      });
      field.onChange(combined.toUTC().toISO());
    }
  }

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 6 }}>
        <Box>
          {dateLabel ? <InputLabel shrink>{dateLabel}</InputLabel> : null}
          <DatePicker
            value={value}
            onChange={handleDateChange}
            slotProps={{
              textField: {
                error: !!fieldState.error,
                helperText: fieldState.error?.message,
                sx: {
                  width: "100%",
                  mb: 0,
                  pt: dateLabel ? 0 : undefined,
                  "& .MuiPickersInputBase-root": {
                    borderRadius: 0,
                    backgroundColor: palette.white,
                  },
                },
              },
            }}
          />
        </Box>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Box>
          {timeLabel ? <InputLabel shrink>{timeLabel}</InputLabel> : null}
          <TimePicker
            value={value}
            onChange={handleTimeChange}
            slotProps={{
              textField: {
                sx: {
                  width: "100%",
                  mb: 0,
                  pt: timeLabel ? 0 : undefined,
                  "& .MuiPickersInputBase-root": {
                    borderRadius: 0,
                    backgroundColor: palette.white,
                  },
                },
              },
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
}
